import type {
	InfraSharedVolume,
	InfraStorage,
	InfraVolumeSnapshot,
	SharedVolumeState,
} from "@/lib/api";
import type { InfraStatusTone } from "@/lib/infra-placement";

export type InfraStorageKind = "sharedVolume" | "volumeSnapshot";

export const INFRA_STORAGE_KINDS: readonly InfraStorageKind[] = ["sharedVolume", "volumeSnapshot"];

export const INFRA_STORAGE_KIND_LABELS: Record<InfraStorageKind, string> = {
	sharedVolume: "共有ボリューム",
	volumeSnapshot: "ボリュームスナップショット",
};

export interface InfraStorageOrganization {
	id: string;
	name: string;
	itemCount: number;
}

/** 共有ボリュームかスナップショットを持つ組織を名前順に並べる。DB に環境行が無く組織の分からないスナップショットは数えない。 */
export function infraStorageOrganizations(storage: InfraStorage): InfraStorageOrganization[] {
	const byId = new Map<string, InfraStorageOrganization>();
	const count = (id: string | null, name: string | null) => {
		if (id === null) return;
		const organization = byId.get(id);
		if (organization) organization.itemCount++;
		else byId.set(id, { id, name: name ?? id, itemCount: 1 });
	};
	for (const volume of storage.sharedVolumes) count(volume.organizationId, volume.organizationName);
	for (const snapshot of storage.volumeSnapshots)
		count(snapshot.organizationId, snapshot.organizationName);
	return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, "ja"));
}

/** 選んだ組織のものだけを残す。組織を選んでいなければ (null) 全件を返す。 */
export function filterStorageByOrganization(
	storage: InfraStorage,
	organizationId: string | null,
): { sharedVolumes: InfraSharedVolume[]; volumeSnapshots: InfraVolumeSnapshot[] } {
	if (organizationId === null)
		return {
			sharedVolumes: [...storage.sharedVolumes],
			volumeSnapshots: [...storage.volumeSnapshots],
		};
	return {
		sharedVolumes: storage.sharedVolumes.filter((v) => v.organizationId === organizationId),
		volumeSnapshots: storage.volumeSnapshots.filter((s) => s.organizationId === organizationId),
	};
}

/** 種類を何も選んでいなければすべて表示する。 */
export function isStorageKindShown(
	selectedKinds: ReadonlySet<InfraStorageKind>,
	kind: InfraStorageKind,
): boolean {
	return selectedKinds.size === 0 || selectedKinds.has(kind);
}

const SHARED_VOLUME_STATUSES: Record<SharedVolumeState, { label: string; tone: InfraStatusTone }> =
	{
		creating: { label: "作成中", tone: "warning" },
		ready: { label: "利用可能", tone: "success" },
		deleting: { label: "削除中", tone: "warning" },
		error: { label: "エラー", tone: "danger" },
	};

export function sharedVolumeStatus(volume: InfraSharedVolume): {
	label: string;
	tone: InfraStatusTone;
} {
	return SHARED_VOLUME_STATUSES[volume.state];
}

export function volumeSnapshotStatus(snapshot: InfraVolumeSnapshot): {
	label: string;
	tone: InfraStatusTone;
} {
	if (snapshot.deleting) return { label: "削除中", tone: "warning" };
	if (snapshot.readyToUse) return { label: "完了", tone: "success" };
	if (snapshot.error !== null) return { label: "失敗", tone: "danger" };
	return { label: "作成中", tone: "warning" };
}
